import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { CourierBadge } from '../ui/primitives';
import { jobsFrom, usePrint } from './PrintProgress';
import { fmtDuration } from '../ui/couriers';
import { API_URL } from '../config/api';

type HistoryRow = {
  id: string;
  order_id: string | number;
  order_name: string;
  customer_name?: string;
  user_name?: string;
  station_name?: string;
  started_at: string;
  completed_at: string;
  total_items?: number;
  packages_count?: number;
};

function DetailsSheet({ row, onClose }: { row: HistoryRow; onClose: () => void }) {
  const { startPrint } = usePrint();
  const { data } = useQuery<any>({
    queryKey: ['kiosk-history-detail', row.id],
    queryFn: async () => (await axios.get(`${API_URL}/api/packing/history/${row.id}`)).data,
  });

  function reprint(kind: 'list' | 'labels') {
    const url = kind === 'list' ? '/api/printing/print-packing-list' : '/api/printing/print-product-labels';
    startPrint({
      title: kind === 'list' ? 'Lista pakowania' : 'Etykiety produktów',
      subtitle: `${row.order_name} · przedruk`,
      unit: kind === 'list' ? 'page' : 'label',
      // historyId: przedruk tego, co spakowano w TEJ sesji — nie całego zamówienia (przy zamówieniu
      // podzielonym w Shopify część wyszła wcześniej, a pozycje usunięte nie wyszły wcale).
      run: async () => jobsFrom((await axios.post(`${API_URL}${url}`, { orderId: row.order_id, historyId: row.id })).data),
    });
  }

  return (
    <div className="scrim" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h3>{row.order_name}</h3>
        <p className="sub">{row.customer_name || '—'} · {row.user_name || '—'} · czas {fmtDuration(row.started_at, row.completed_at)}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {(data?.packages || []).map((p: any, i: number) => (
            <div className="pkg-card" key={i}>
              <div className="ph"><span>Paczka {i + 1}{p.packageType?.name ? ` · ${p.packageType.name}` : ''}</span>{p.carrier && <CourierBadge courier={String(p.carrier).toLowerCase() === 'dhl' ? 'dhl' : String(p.carrier).toLowerCase() === 'goodspeed' ? 'goodspeed' : 'inpost'} />}</div>
              <div className="pmeta">
                {p.dimensions ? `${p.dimensions.length}×${p.dimensions.width}×${p.dimensions.height} cm · ` : ''}
                {p.weight ? `${p.weight} kg` : ''}
              </div>
              {p.trackingNumber && <div className="ptrack" style={{ color: 'var(--success-text)' }}><Icon name="check-circle-2" size={13} /> {p.trackingNumber}</div>}
            </div>
          ))}
          {data && !(data.packages || []).length && <div className="muted" style={{ fontSize: 13 }}>Brak danych o paczkach.</div>}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="big-btn bb-ghost" style={{ flex: 1 }} onClick={() => reprint('list')}><Icon name="file-text" size={18} /> List pakowania</button>
          <button className="big-btn bb-primary" style={{ flex: 1 }} onClick={() => reprint('labels')}><Icon name="printer" size={18} /> Przedrukuj etykiety</button>
        </div>
      </div>
    </div>
  );
}

export default function DoneScreen() {
  const navigate = useNavigate();
  const [open, setOpen] = useState<HistoryRow | null>(null);

  const { data: rows = [] } = useQuery<HistoryRow[]>({
    queryKey: ['kiosk-history'],
    queryFn: async () => (await axios.get(`${API_URL}/api/packing/history`)).data,
    refetchInterval: 30000,
  });

  const today = new Date().toDateString();
  const todayRows = rows.filter((r) => r.completed_at && new Date(r.completed_at).toDateString() === today);
  const list = todayRows.length ? todayRows : rows;

  return (
    <div className="screen">
      <div className="k-header">
        <button className="back" onClick={() => navigate('/packing-station/dashboard')}><Icon name="arrow-left" size={20} /></button>
        <div><div className="k-title">Zakończone dziś</div><div className="k-sub">{list.length} zamówień</div></div>
      </div>
      <div className="scrollable" style={{ flex: 1 }}>
        {list.length === 0 && (
          <div className="state-msg">
            <div className="si"><Icon name="check-check" size={30} /></div>
            <h3>Nic jeszcze nie spakowano</h3><p>Zakończone zamówienia pojawią się tutaj.</p>
          </div>
        )}
        {list.map((r) => (
          <div className="done-row" key={r.id} style={{ cursor: 'pointer' }} onClick={() => setOpen(r)}>
            <div className="ck"><Icon name="check" size={18} /></div>
            <div>
              <div className="dn">{r.order_name}</div>
              <div className="dmeta">
                {r.customer_name || '—'} · {r.user_name || '—'} · {r.completed_at ? new Date(r.completed_at).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) : '—'} · {r.packages_count || 1} {r.packages_count === 1 ? 'paczka' : 'paczki'}
              </div>
            </div>
            <div className="dtrack" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <span>{fmtDuration(r.started_at, r.completed_at)}</span>
              <Icon name="chevron-right" size={16} style={{ color: 'var(--fg-3)' }} />
            </div>
          </div>
        ))}
      </div>

      {open && <DetailsSheet row={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
